"use client";

import { useEffect, useState } from "react";
import { Plus, ChevronLeft, Trash2 } from "lucide-react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import Input, { InputWithIcon } from "../../components/partials/inputs";
import { apiCall } from "../../data/useFetcher";
import toast from "react-hot-toast";
import { useUsersStore } from "../../data/stores/loggerStore";
import useAuthStore from "../../data/stores/authStore";
import { useNavigate } from "react-router-dom";

interface Department {
  id: string;
  departmentName: string;
  email: string;
  erpModule: string;
}

interface Organization {
  organizationName: string;
  email: string;
  country: string;
}

interface FormData {
  organization: Organization;
  departments: Department[];
}

export default function DepartmentForm() {
  const navigate = useNavigate();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { token } = useAuthStore();
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    // watch,
  } = useForm<FormData>({
    defaultValues: {
      organization: {
        organizationName: "",
        email: "",
        country: "",
      },
      departments: [{ id: "1", departmentName: "", email: "", erpModule: "" }],
    },
  });

  const { getDynamicLogger, regionCountry }: any = useUsersStore();

  useEffect(() => {
    apiCall({
      type: "get",
      url: `/api/v1/data-system/manage-region-country?pagination=not&useHardCoded=true`,
      getter: (d: any) => getDynamicLogger(d, "regionCountry"),
    });
  }, []);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "departments",
  });

  const addDepartment = () => {
    append({
      id: Date.now().toString(),
      departmentName: "",
      email: "",
      erpModule: "",
    });
  };

  const deleteDepartment = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    try {
      // Remove the id field from departments before submitting
      const submitData = {
        organization: data.organization,
        departments: data.departments.map(({ id, ...dept }) => dept),
      };

      console.log("Submitting data:", submitData);

      const { response, errMsg } = await apiCall({
        type: "post",
        url: `/api/v1/auth/user/create-organization-with-departments`,
        data: submitData,
        headers: {
          "Content-Type": "application/json",
          ...(token
            ? { Authorization: token, "frontend-source": "webadmin" }
            : {}),
        },
        noToast: true,
      });

      if (response) {
        console.log("Success:", response);
        // Clear the form
        reset({
          organization: {
            organizationName: "",
            email: "",
            country: "",
          },
          departments: [
            { id: "1", departmentName: "", email: "", erpModule: "" },
          ],
        });
        // Show success modal
        setShowSuccessModal(true);
      } else {
        console.error("Error:", errMsg);
        toast.error(
          errMsg || "Failed to create organization. Please try again."
        );
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-2 sm:gap-4 mb-6 sm:mb-8">
          <button
            onClick={() => navigate("/invitation")}
            className="p-2 hover:bg-gray-200 rounded-lg transition"
          >
            <ChevronLeft size={20} className="text-gray-700 sm:w-6 sm:h-6" />
          </button>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
            Create Organization & Departments
          </h1>
          {/* <Avatar className="h-12 w-12 ml-auto">
            <AvatarFallback className="bg-orange-500 text-white font-bold text-lg">
              K
            </AvatarFallback>
          </Avatar> */}
        </div>

        {/* Instructions */}
        <p className="text-gray-600 mb-6 sm:mb-8 text-sm sm:text-base">
          Enter organization and department details correctly.
        </p>

        {/* Form */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-6 sm:space-y-8 w-full"
        >
          {/* Organization Information */}
          <div className="bg-blue-50 p-4 sm:p-6 rounded-lg">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
              Organization Information
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {/* Organization Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Organization Name <span className="text-red-500">*</span>
                </label>
                <Controller
                  name="organization.organizationName"
                  control={control}
                  rules={{ required: "Organization name is required" }}
                  render={({ field }) => (
                    <Input
                      type="text"
                      {...field}
                      placeholder="e.g., Ministry of Water Resources"
                    />
                  )}
                />
                {errors.organization?.organizationName && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.organization.organizationName.message}
                  </p>
                )}
              </div>

              {/* Organization Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Organization Email <span className="text-red-500">*</span>
                </label>
                <Controller
                  name="organization.email"
                  control={control}
                  rules={{
                    required: "Organization email is required",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Invalid email format",
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      type="email"
                      {...field}
                      placeholder="e.g., admin@waterministry.gov"
                    />
                  )}
                />
                {errors.organization?.email && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.organization.email.message}
                  </p>
                )}
              </div>

              {/* Country */}
              <div>
                <Controller
                  name="organization.country"
                  control={control}
                  rules={{ required: "Country is required" }}
                  render={({ field: { value, onChange, name } }) => (
                    <InputWithIcon
                      icon="mdi:web"
                      placeholder="Country"
                      label="Country of Operation"
                      value={value}
                      onChange={onChange}
                      name={name}
                      type="select"
                      options={regionCountry?.docs
                        ?.find((it: any) => it?.category === "COUNTRY")
                        ?.data?.sort(
                          (
                            a: { title: { toString: () => string } },
                            b: { title: { toString: () => any } }
                          ) => {
                            // Compare the values of the specified key in a case-insensitive manner
                            return a?.title
                              ?.toString()
                              ?.localeCompare(b?.title?.toString());
                          }
                        )
                        ?.map((it: any) => {
                          return {
                            ...it,
                            _id: it?.title,
                          };
                        })}
                    />
                  )}
                />
                {errors.organization?.country && (
                  <p className="text-[#dc2626] text-xs mt-4">
                    {errors.organization.country.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Departments Section */}
          <div className="bg-blue-50 p-4 sm:p-6 rounded-lg">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
              Departments
            </h3>
            <div className="space-y-4 sm:space-y-6">
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="flex flex-col sm:flex-row gap-4 items-start"
                >
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Department */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Department<span className="text-red-500">*</span>
                      </label>
                      <Controller
                        name={`departments.${index}.departmentName`}
                        control={control}
                        rules={{ required: "Department name is required" }}
                        render={({ field }) => (
                          <Input
                            type="text"
                            placeholder="Enter department"
                            {...field}
                          />
                        )}
                      />
                      {errors.departments?.[index]?.departmentName && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.departments[index]?.departmentName?.message}
                        </p>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Email<span className="text-red-500">*</span>
                      </label>
                      <Controller
                        name={`departments.${index}.email`}
                        control={control}
                        rules={{
                          required: "Email is required",
                          pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            message: "Invalid email format",
                          },
                        }}
                        render={({ field }) => (
                          <Input
                            type="email"
                            placeholder="Enter Email"
                            {...field}
                          />
                        )}
                      />
                      {errors.departments?.[index]?.email && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.departments[index]?.email?.message}
                        </p>
                      )}
                    </div>

                    {/* ERP Module */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        ERP Modules / Data Systems Used
                        <span className="text-red-500">*</span>
                      </label>
                      <Controller
                        name={`departments.${index}.erpModule`}
                        control={control}
                        rules={{ required: "ERP Module is required" }}
                        render={({ field }) => (
                          <Input
                            type="text"
                            placeholder="Enter Module"
                            {...field}
                          />
                        )}
                      />
                      {errors.departments?.[index]?.erpModule && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.departments[index]?.erpModule?.message}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Delete Button */}
                  <button
                    type="button"
                    onClick={() => deleteDepartment(index)}
                    disabled={fields.length === 1}
                    className={`mt-4 sm:mt-8 p-2 rounded-lg transition self-start ${
                      fields.length === 1
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-red-500 hover:bg-red-50"
                    }`}
                    aria-label="Delete department"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              ))}
            </div>

            {/* Add Another Department */}
            <button
              type="button"
              onClick={addDepartment}
              className="flex items-center mt-2 gap-2 text-blue-600 hover:text-blue-700 font-medium transition text-sm sm:text-base"
            >
              <Plus size={18} className="sm:w-5 sm:h-5" />
              Add another department
            </button>
          </div>

          {/* Continue Button */}
          <div className="pt-4 sm:pt-6">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full font-semibold py-3 px-4 rounded-lg transition text-sm sm:text-base flex items-center justify-center gap-2 ${
                isSubmitting
                  ? "bg-gray-400 cursor-not-allowed text-white"
                  : "bg-blue-600 hover:bg-blue-700 text-white"
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Creating Organization...
                </>
              ) : (
                "Create Organization"
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              {/* Success Icon */}
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <svg
                  className="h-8 w-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>

              {/* Success Message */}
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Organization Created Successfully!
              </h3>
              <p className="text-gray-600 mb-6">
                Your organization and departments have been created. You can now
                go back to the invitation page to update your data systems.
              </p>

              {/* Action Button */}
              <div className="flex justify-center">
                <button
                  onClick={() => navigate("/invitation")}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Go to Invitations
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
